if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.addButton = this.querySelector('[name="add"]');
        this.submitButtonText = this.addButton?.querySelector('span');
        this.hideErrors = this.dataset.hideErrors === 'true';

        if (!this.form) return;
        if (this.variantIdInput) this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        if (document.querySelector('cart-drawer')) {
          this.querySelectorAll('[type="submit"]').forEach((button) => button.setAttribute('aria-haspopup', 'dialog'));
        }
      }

      async onSubmitHandler(event) {
        event.preventDefault();
        const submitter = event.submitter || this.addButton;
        if (!submitter || submitter.disabled || submitter.getAttribute('aria-disabled') === 'true') return;

        const cart = window.AbokStorefrontCart;
        if (!cart) {
          this.handleErrorMessage(window.cartStrings?.error || 'The cart is unavailable.');
          return;
        }

        const buyNow = submitter.hasAttribute('data-storefront-buy-now');
        const variantId = this.variantIdInput?.value;
        const quantity = Number.parseInt(new FormData(this.form).get('quantity'), 10) || 1;
        const purchase = this.collectPurchaseLines(buyNow ? 'buy-now' : 'add', variantId, quantity);
        const linesUpdateDeferred = buyNow
          ? null
          : this.createCartLinesUpdateEvent(variantId, quantity, purchase.extraLines);

        this.handleErrorMessage();
        this.setLoading(submitter, true);
        document.querySelector('cart-drawer')?.setActiveElement?.(submitter);

        try {
          if (buyNow) {
            await cart.buyNowFromForm(this.form, {
              source: 'buy-now-button',
              trigger: submitter,
              extraLines: purchase.extraLines,
              validate: async (snapshot) => {
                const settlement = await this.validatePurchase(snapshot, purchase, 'buy-now');
                return settlement.snapshot;
              },
            });
            this.dispatchPurchaseResult(purchase, 'buy-now', 'success');
            return;
          }

          const snapshot = await cart.addFromForm(this.form, {
            source: 'product-form',
            trigger: submitter,
            extraLines: purchase.extraLines,
          });
          const settlement = await this.validatePurchase(snapshot, purchase, 'add');
          this.resolveCartLinesUpdate(linesUpdateDeferred);
          if (settlement.warning) this.handleErrorMessage(settlement.warning);
          this.dispatchPurchaseResult(purchase, 'add', settlement.warning ? 'partial' : 'success');
          this.openCartSurface(submitter);
        } catch (error) {
          this.handleErrorMessage(error?.message || window.cartStrings?.error);
          this.dispatchCartErrorEvent(error?.message || window.cartStrings?.error, error?.code || 'SERVICE_UNAVAILABLE');
          this.dispatchPurchaseResult(purchase, buyNow ? 'buy-now' : 'add', 'failure', error);
          linesUpdateDeferred?.reject(error);
        } finally {
          this.setLoading(submitter, false);
          window.CartPerformance?.measureFromEvent?.('add:user-action', event);
        }
      }

      openCartSurface(trigger) {
        const quickAddModal = this.closest('quick-add-modal');
        const open = () => {
          const drawer = document.querySelector('cart-drawer');
          if (drawer?.open) {
            drawer.open(trigger);
            return;
          }
          const notification = document.querySelector('cart-notification');
          if (notification?.open) {
            notification.setActiveElement?.(trigger);
            notification.open();
            return;
          }
          window.location.assign(window.routes?.cart_url || '/cart');
        };

        if (!quickAddModal) {
          open();
          return;
        }

        document.body.addEventListener('modalClosed', open, { once: true });
        quickAddModal.hide(true);
      }

      setLoading(button, loading) {
        button.classList.toggle('loading', loading);
        button.toggleAttribute('aria-disabled', loading);
        button.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
      }

      handleErrorMessage(errorMessage = '') {
        if (this.hideErrors) return;
        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        this.errorMessage = this.errorMessage || this.errorMessageWrapper?.querySelector('.product-form__error-message');
        if (!this.errorMessageWrapper || !this.errorMessage) return;
        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        this.errorMessage.textContent = errorMessage || '';
      }

      toggleSubmitButton(disable = true, text) {
        this.querySelectorAll('.product-form__submit').forEach((button) => {
          button.toggleAttribute('disabled', disable);
        });
        if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        if (!disable && this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
      }

      collectPurchaseLines(mode, variantId, quantity) {
        const requestId = window.crypto?.randomUUID?.()
          || `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
        const detail = {
          version: 1,
          form: this.form,
          mode,
          requestId,
          mainVariantId: variantId,
          mainQuantity: quantity,
          extraLines: [],
          claims: [],
        };
        this.dispatchEvent(new CustomEvent('abok:purchase-lines:collect', { bubbles: true, detail }));
        return detail;
      }

      async validatePurchase(snapshot, purchase, mode) {
        const detail = {
          version: 1,
          mode,
          requestId: purchase.requestId,
          snapshot,
          claims: purchase.claims,
          settlements: [],
        };
        this.dispatchEvent(new CustomEvent('abok:purchase:validate', { bubbles: true, detail }));

        let currentSnapshot = snapshot;
        const warnings = [];
        for (const settlement of detail.settlements) {
          const result = await settlement;
          if (result?.snapshot) currentSnapshot = result.snapshot;
          if (result?.warning) warnings.push(result.warning);
        }
        return { snapshot: currentSnapshot, warning: warnings.join(' ') };
      }

      createCartLinesUpdateEvent(variantId, quantity, extraLines = []) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent || !variantId) return null;
        const merchandiseId = String(variantId).startsWith('gid://')
          ? String(variantId)
          : `gid://shopify/ProductVariant/${variantId}`;
        const lines = [{ merchandiseId, quantity }].concat(
          extraLines.map((line) => {
            const rawId = String(line.merchandiseId || line.id || line.variantId || '');
            return {
              merchandiseId: rawId.startsWith('gid://') ? rawId : `gid://shopify/ProductVariant/${rawId}`,
              quantity: Number.parseInt(line.quantity, 10) || 1,
            };
          })
        );
        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines,
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        const cartData = window.AbokStorefrontCart?.toDawnCart?.();
        if (!CartLinesUpdateEvent || !cartData) return;
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartData) });
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      dispatchPurchaseResult(purchase, mode, status, error = null) {
        const lineVariantIds = [purchase.mainVariantId]
          .concat(purchase.extraLines.map((line) => line.merchandiseId || line.id || line.variantId))
          .filter(Boolean);
        this.dispatchEvent(new CustomEvent('abok:purchase-result', {
          bubbles: true,
          detail: {
            version: 1,
            mode,
            status,
            errorCode: error?.code || '',
            lineVariantIds,
          },
        }));
      }

      get variantIdInput() {
        return this.form?.querySelector('[name="id"]');
      }
    }
  );
}
